import { AlertTriangle, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { FeeTableCard } from "@/components/admin/FeeTableCard";
import { FishingEditor } from "@/components/admin/FishingEditor";
import { ExtrasEditor } from "@/components/admin/ExtrasEditor";
import { OtherTypesEditor } from "@/components/admin/OtherTypesEditor";

const Toggle = ({ label, hint, checked, onChange, testId, locked }) => (
  <label className={`flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-white px-4 py-3 transition-colors ${locked ? "cursor-not-allowed" : "cursor-pointer hover:border-sky-300"}`}>
    <div>
      <div className="text-sm font-medium text-slate-900">{label}</div>
      <div className="text-xs text-slate-500">{hint}</div>
    </div>
    <Switch checked={!!checked} onCheckedChange={onChange} disabled={locked} data-testid={testId} />
  </label>
);

export const VesselTypes = ({ c, save, newTable, part }) => {
  const updateCruise = (i, t) => save("cruise_tables", c.cruise_tables.map((x, j) => (j === i ? t : x)));
  const toggle = (field, dataField) => (v) => {
    save(field, v, 0);
    if (v) save(dataField, c[dataField], 0);
  };
  const on = { general: c.general_enabled !== false, cruise: !!c.cruise_enabled, fishing: !!c.fishing_enabled, leisure: !!c.leisure_enabled, other: !!c.other_enabled && c.other_groups.some((g) => g.types.length), custom: (c.custom_calcs || []).some((x) => x.enabled !== false) };
  const last = Object.values(on).filter(Boolean).length <= 1;
  const lock = (k) => last && on[k];
  return (
    <div className="space-y-5">
      {part !== "tables" && (<>
      <div className="grid sm:grid-cols-2 gap-3">
        <Toggle label="Generelle fartøy" hint="Satser etter brutto tonnasje og antall døgn" checked={c.general_enabled !== false} onChange={(v) => save("general_enabled", v, 0)} testId="general-enabled-switch" locked={lock("general")} />
        <Toggle label="Cruiseskip" hint="Egne satser for farvannsavgift og kaivederlag" checked={c.cruise_enabled} onChange={toggle("cruise_enabled", "cruise_tables")} testId="cruise-enabled-switch" locked={lock("cruise")} />
        <Toggle label="Fiskefartøy" hint="Pris per døgn etter lengde, med rabatt per periode" checked={c.fishing_enabled} onChange={toggle("fishing_enabled", "fishing")} testId="fishing-enabled-switch" locked={lock("fishing")} />
        <Toggle label="Fritidsfartøy" hint="Pris etter lengde, med/uten strøm og korttidsopphold" checked={c.leisure_enabled} onChange={toggle("leisure_enabled", "leisure")} testId="leisure-enabled-switch" locked={lock("leisure")} />
        <Toggle label="Andre fartøystyper" hint="Last, passasjerfartøy og spesielle oppdrag, med egne priser" checked={c.other_enabled} onChange={toggle("other_enabled", "other_groups")} testId="other-enabled-switch" locked={lock("other")} />
      </div>
      <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="vessel-types-warning">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
        <span>Minst én kalkulator må være slått på. Hvis alle slås av, forsvinner kalkulatoren fra nettsiden.{last && " Den siste påslåtte kan derfor ikke slås av."}</span>
      </div>
      <p className="text-xs text-slate-500">Når en av disse er slått på, får kalkulatoren en nedtrekksmeny der «Generelle fartøy» er valgt som standard.</p>
      <ExtrasEditor c={c} save={save} />
      </>)}
      {part !== "switches" && (<>
      {c.cruise_enabled && (
        <div className="space-y-4" data-testid="cruise-section">
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-display text-lg font-semibold text-slate-900">Cruiseskip</h3>
            <Button size="sm" variant="outline" onClick={() => save("cruise_tables", [...c.cruise_tables, newTable()], 0)} data-testid="add-cruise-table-button"><Plus className="w-4 h-4 mr-1" />Ny tabell</Button>
          </div>
          {c.cruise_tables.map((t, i) => (
            <FeeTableCard key={t.id} table={t} onChange={(nt) => updateCruise(i, nt)} canDelete={c.cruise_tables.length > 1}
              onDelete={() => save("cruise_tables", c.cruise_tables.filter((_, j) => j !== i), 0)} />
          ))}
        </div>
      )}
      {c.fishing_enabled && (
        <div className="space-y-4">
          <h3 className="font-display text-lg font-semibold text-slate-900">Fiskefartøy</h3>
          <FishingEditor fishing={c.fishing} onChange={(f) => save("fishing", f)} />
        </div>
      )}
      {c.leisure_enabled && (
        <div className="space-y-4">
          <h3 className="font-display text-lg font-semibold text-slate-900">Fritidsfartøy</h3>
          <FishingEditor fishing={c.leisure} onChange={(f) => save("leisure", f)} prefix="leisure" />
        </div>
      )}
      {c.other_enabled && <OtherTypesEditor c={c} save={save} newTable={newTable} />}
      </>)}
    </div>
  );
};
